"use client";

import { useMemo, useState, useEffect, useRef } from "react";
import Header from "../components/Header";
import RequireAuth from "../components/RequireAuth";
import SearchableSelect from "../components/SearchableSelect";
import { addListingAsync, getMySellerId } from "../lib/storage";
import { showToast } from "../lib/toast";
import { Category, Condition, Listing } from "../lib/types";
import { auth } from "../lib/firebase";
import { onAuthStateChanged } from "firebase/auth";
import * as firebaseStorage from "../lib/firebase-storage";
import Avatar from "../components/Avatar";
import { trackEvent } from "../lib/analytics";

import { CITIES_BIH } from "../lib/cities";

const SIZES = [
  "XS", "S", "M", "L", "XL", "XXL", "XXXL",
  // Dječija odjeća (EU visine i klasične dječije veličine)
  "50", "56", "62", "68", "74", "80", "86", "92", "98",
  "104", "110", "116", "122", "128", "134", "140", "146",
  "152", "158", "164", "170", "176",
  "0-3 mjeseca", "3-6 mjeseci", "6-9 mjeseci", "9-12 mjeseci",
  "12-18 mjeseci", "18-24 mjeseca",
  "2-3 godine", "3-4 godine", "4-5 godina", "5-6 godina",
  "6-7 godina", "7-8 godina", "8-9 godina", "9-10 godina",
  "10-11 godina", "11-12 godina", "12-13 godina", "13-14 godina", "14-15 godina",
  // Odrasle / univerzalne veličine
  "28", "30", "32", "34", "35", "36", "37", "38", "39", "40",
  "41", "42", "43", "44", "45", "46", "48", "52",
  "Jedna veličina", "Univerzalna veličina"
];
const COLORS = [
  "Crna", "Bijela", "Siva", "Bež", "Crvena", "Bordo", "Plava", "Zelena", "Roza", "Smeđa",
  "Narandžasta", "Žuta", "Ljubičasta", "Tirkizna", "Koralna", "Breskva", "Menta", "Krem",
  "Crvenkasta", "Tamno plava", "Svijetlo plava", "Tamno siva", "Svijetlo siva",
  "Zlatna", "Srebrna", "Bronzana", "Multicolor", "Print", "Prugasta", "Točkasta"
];
const TYPES = [
  "Abaja", "Aksesuar", "Baletanke", "Bermuda", "Bluza", "Bodysuit",
  "Čizme", "Čizme do koljena", "Cipele",
  "Dugačke hlače", "Duks", "Džemper", "Džilbab",
  "Farmerke",
  "Haljina", "Hidžab", "Hlače",
  "Jakna", "Jeans",
  "Kaftan", "Kaput", "Kardigan", "Kapa", "Kimono", "Kladionica", "Kombinezon", "Košulja", "Kratke hlače",
  "Lanac", "Leggings", "Loafers",
  "Majica", "Mokasine",
  "Naočare", "Narukvica", "Naušnice", "Niqab", "Novčanik",
  "Odijelo",
  "Pantalone", "Patike", "Pidžama", "Portfelj", "Prsten", "Pulover",
  "Ranac", "Rukavice", "Ruksak",
  "Sako", "Sandale", "Šal", "Šešir", "Šorc", "Suknja",
  "Top", "Torba", "Torbica", "Trenerica", "Trenerka", "Tunika",
  "Vesta"
];
const BRANDS = [
  "Zara", "Bershka", "Stradivarius", "Pull&Bear", "H&M", "Mango", "Valentino", "Nike", "Adidas", "Puma", "Massimo Dutti",
  "Calvin Klein", "Tommy Hilfiger", "Ralph Lauren", "Guess", "Levi's", "Diesel", "Armani", "Hugo Boss",
  "Lacoste", "Polo", "Vans", "Converse", "New Balance", "Reebok", "Fila", "Champion",
  "Reserved", "Mohito", "Sinsay", "House", "C&A", "Peek & Cloppenburg"
];

const MAX_DESCRIPTION_LENGTH = 1000;
const MAX_IMAGE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB
const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const ALLOWED_VIDEO_TYPES = ["video/mp4", "video/webm", "video/quicktime"];

type ValidationErrors = {
  title?: string;
  price?: string;
  imageUrl?: string;
  description?: string;
  agree?: string;
};

export default function AddPage() {
  const [agree, setAgree] = useState(false);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState<Category | "">("");
  const [productType, setProductType] = useState("");
  const [brand, setBrand] = useState("");
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [condition, setCondition] = useState<Condition | "">("");
  const [city, setCity] = useState("");
  const [price, setPrice] = useState<string>("");
  const [allowsOffers, setAllowsOffers] = useState<boolean>(false);
  const [allowsTrade, setAllowsTrade] = useState<boolean>(false);
  const [description, setDescription] = useState("");

  const [imageUrls, setImageUrls] = useState<string[]>([]);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const [selectedVideo, setSelectedVideo] = useState<File | null>(null);
  const [videoPreview, setVideoPreview] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<{ [key: number]: number }>({});
  const [isUploading, setIsUploading] = useState<{ [key: number]: boolean }>({});
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [username, setUsername] = useState<string>("korisnik");
  const [showPreview, setShowPreview] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const publishLockRef = useRef(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          // Try to get username from profile, fallback to displayName
          const { getProfileAsync } = await import("../lib/storage");
          const { getDisplayName } = await import("../lib/firebase-storage");
          const profile = await getProfileAsync(user.uid);
          // Use username if available, otherwise use displayName
          const usernameToUse = profile.username || getDisplayName(profile, user.uid);
          setUsername(usernameToUse);
        } catch (error) {
          // Fallback to Firebase auth displayName or email
          const displayName = user.displayName || user.email?.split("@")[0] || "korisnik";
          setUsername(displayName);
        }
      }
    });
    return () => unsub();
  }, []);

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    // Title validation
    if (!title.trim()) {
      newErrors.title = "Naslov oglasa je obavezan";
    } else if (title.trim().length < 3) {
      newErrors.title = "Naslov mora imati najmanje 3 karaktera";
    } else if (title.trim().length > 100) {
      newErrors.title = "Naslov ne može biti duži od 100 karaktera";
    }

    // Price validation (only if allowsOffers and allowsTrade are false)
    if (!allowsOffers && !allowsTrade) {
      const p = Number(price);
      if (!price.trim()) {
        newErrors.price = "Cijena je obavezna";
      } else if (!Number.isFinite(p)) {
        newErrors.price = "Cijena mora biti validan broj";
      } else if (p <= 0) {
        newErrors.price = "Cijena mora biti veća od 0";
      } else if (p > 100000) {
        newErrors.price = "Cijena ne može biti veća od 100.000 KM";
      }
    }

    // Description validation
    if (description.trim().length > MAX_DESCRIPTION_LENGTH) {
      newErrors.description = `Opis ne može biti duži od ${MAX_DESCRIPTION_LENGTH} karaktera`;
    }

    // Image validation
    if (imageUrls.length === 0) {
      newErrors.imageUrl = "Najmanje jedna slika je obavezna";
    }

    // Agreement validation
    if (!agree) {
      newErrors.agree = "Morate prihvatiti uslove korištenja";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onPickImages = async (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    const newFiles = Array.from(files);
    const maxImages = 10;
    
    if (imageUrls.length + newFiles.length > maxImages) {
      setErrors({ ...errors, imageUrl: `Možete dodati najviše ${maxImages} slika` });
      return;
    }

    const userId = auth.currentUser?.uid || getMySellerId();
    if (!userId) {
      setErrors({ ...errors, imageUrl: "Korisnik nije prijavljen" });
      return;
    }

    // Upload each image to Firebase Storage
    for (let index = 0; index < newFiles.length; index++) {
      const file = newFiles[index];
      
      // Validate file type
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setErrors({ ...errors, imageUrl: "Dozvoljeni formati: JPG, PNG, WEBP" });
        continue;
      }

      // Validate file size
      if (file.size > MAX_IMAGE_SIZE) {
        setErrors({ ...errors, imageUrl: "Svaka slika ne može biti veća od 5MB" });
        continue;
      }

      const imageIndex = imageUrls.length + index;
      setIsUploading((prev) => ({ ...prev, [imageIndex]: true }));
      setUploadProgress((prev) => ({ ...prev, [imageIndex]: 0 }));
      setErrors({ ...errors, imageUrl: undefined });

      // Use uploadListingImageWithProgress which compresses images before converting to base64
      // This ensures images fit within Firestore's 1MB document size limit
      (async () => {
        try {
          setUploadProgress((prev) => ({ ...prev, [imageIndex]: 10 }));
          
          const base64Url = await firebaseStorage.uploadListingImageWithProgress(
            file,
            "",
            userId,
            (progress) => {
              setUploadProgress((prev) => ({ ...prev, [imageIndex]: progress }));
            }
          );
          
          // Add base64 URL to imageUrls
          setImageUrls((prev) => [...prev, base64Url]);
          
          // Mark as completed
          setIsUploading((prev) => ({ ...prev, [imageIndex]: false }));
          setUploadProgress((prev) => ({ ...prev, [imageIndex]: 100 }));
          
          // Clear progress after a short delay
          setTimeout(() => {
            setUploadProgress((prev) => {
              const newProgress = { ...prev };
              delete newProgress[imageIndex];
              return newProgress;
            });
          }, 500);
        } catch (error: any) {
          console.error(`[onPickImages] Error processing image ${index + 1}:`, error);
          showToast.error(error?.message || "Greška pri učitavanju slike");
          setErrors({ ...errors, imageUrl: error?.message || "Greška pri učitavanju slike" });
          setIsUploading((prev) => {
            const newUploading = { ...prev };
            delete newUploading[imageIndex];
            return newUploading;
          });
          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[imageIndex];
            return newProgress;
          });
          // Remove failed image
          setImageUrls((prev) => prev.filter((_, i) => i !== imageIndex));
        }
      })();
    }
  };

  const removeImage = (index: number) => {
    setImageUrls((prev) => prev.filter((_, i) => i !== index));
    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[index];
      return newProgress;
    });
    setIsUploading((prev) => {
      const newUploading = { ...prev };
      delete newUploading[index];
      return newUploading;
    });
    setErrors({ ...errors, imageUrl: undefined });
  };

  // Drag and drop handlers for reordering images
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (draggedIndex === null) return;
    
    const newImageUrls = [...imageUrls];
    const draggedItem = newImageUrls[draggedIndex];
    
    // Remove dragged item
    newImageUrls.splice(draggedIndex, 1);
    // Insert at new position
    newImageUrls.splice(dropIndex, 0, draggedItem);
    
    setImageUrls(newImageUrls);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Drag and drop handlers for file upload
  const handleDropFiles = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    if (Object.values(isUploading).some(v => v)) return;
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      const imageFiles = fileArray.filter(file => ALLOWED_IMAGE_TYPES.includes(file.type));
      
      if (imageFiles.length > 0) {
        // Create a FileList-like object
        const dataTransfer = new DataTransfer();
        imageFiles.forEach(file => dataTransfer.items.add(file));
        onPickImages(dataTransfer.files);
      }
    }
  };

  const handleDragOverFiles = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeaveFiles = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const onPickVideo = async (file: File | null) => {
    if (!file) {
      setVideoUrl("");
      setSelectedVideo(null);
      setVideoPreview(null);
      setIsUploadingVideo(false);
      return;
    }

    // Validate file type
    if (!ALLOWED_VIDEO_TYPES.includes(file.type)) {
      showToast.error("Dozvoljeni formati: MP4, WebM, MOV");
      return;
    }

    // Validate file size
    if (file.size > MAX_VIDEO_SIZE) {
      showToast.error("Video ne može biti veći od 50MB");
      return;
    }

    setSelectedVideo(file);
    setIsUploadingVideo(true);

    // Create preview
    const reader = new FileReader();
    reader.onload = () => {
      setVideoPreview(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload video to Firebase Storage
    try {
      const userId = auth.currentUser?.uid || getMySellerId();
      if (!userId) {
        throw new Error("Korisnik nije prijavljen");
      }
      
      const uploadedUrl = await firebaseStorage.uploadListingVideo(file, "", userId);
      setVideoUrl(uploadedUrl);
      setIsUploadingVideo(false);
      showToast.success("Video je uspješno učitavan.");
    } catch (error: any) {
      console.error("Error uploading video:", error);
      showToast.error(error?.message || "Greška pri učitavanju videa.");
      setIsUploadingVideo(false);
      setSelectedVideo(null);
      setVideoPreview(null);
    }
  };

  const removeVideo = () => {
    setVideoUrl("");
    setSelectedVideo(null);
    setVideoPreview(null);
    setIsUploadingVideo(false);
  };

  const handlePreview = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setShowPreview(true);
  };

  const handlePublish = async () => {
    if (publishLockRef.current) return;
    publishLockRef.current = true;
    setIsPublishing(true);

    // Check if any images are still uploading
    const stillUploading = Object.values(isUploading).some(v => v === true);
    if (stillUploading) {
      showToast.error("Molimo sačekajte da se sve slike završe učitavanje prije objave.");
      publishLockRef.current = false;
      setIsPublishing(false);
      return;
    }

    const p = (allowsOffers || allowsTrade) ? 0 : Number(price);
    const finalBrand = brand.trim();
    if (!finalBrand) {
      showToast.error("Marka je obavezna");
      publishLockRef.current = false;
      setIsPublishing(false);
      return;
    }
    // Ensure we have at least one image
    if (imageUrls.length === 0) {
      showToast.error("Morate dodati najmanje jednu sliku prije objave.");
      publishLockRef.current = false;
      setIsPublishing(false);
      return;
    }

    // Check total document size (Firestore limit is 1MB)
    // Estimate size: base64 strings + other fields
    const totalImageSize = imageUrls.reduce((sum, url) => sum + (url?.length || 0), 0);
    const estimatedDocSize = totalImageSize + JSON.stringify({
      title, category, productType, brand: finalBrand, size, color, condition, city, price: p, description, username, allowsOffers, allowsTrade
    }).length;
    
    console.log("[handlePublish] Document size estimate:", {
      imageSize: totalImageSize,
      otherFields: estimatedDocSize - totalImageSize,
      total: estimatedDocSize,
      limit: 1048576
    });
    
    if (estimatedDocSize > 900000) { // Leave some margin (900KB instead of 1MB)
      showToast.error(`Oglas je prevelik (${Math.round(estimatedDocSize / 1024)}KB). Molimo smanjite broj slika ili koristite manje slike.`);
      publishLockRef.current = false;
      setIsPublishing(false);
      return;
    }

    const item: Listing = {
      id: crypto.randomUUID(),
      title: title.trim(),
      category: category as Category,
      productType,
      brand: finalBrand,
      size,
      color,
      condition: condition as Condition,
      city,
      price: p,
      allowsOffers: allowsOffers || undefined,
      allowsTrade: allowsTrade || undefined,
      description: description.trim(),
      imageUrl: imageUrls[0] || "",
      imageUrls: imageUrls.length > 0 ? imageUrls : [imageUrls[0] || ""],
      videoUrl: videoUrl || undefined,
      sellerId: getMySellerId(),
      username,
      createdAt: Date.now(),
    };

    console.log("[handlePublish] Publishing listing with images:", {
      imageUrl: item.imageUrl,
      imageUrls: item.imageUrls,
      imageUrlsCount: item.imageUrls?.length || 0
    });

    try {
      const newId = await addListingAsync(item);
      console.log("[handlePublish] Listing published successfully");
      showToast.success("Oglas je uspješno objavljen!");
      const u = auth.currentUser;
      trackEvent("ad_create", {
        ad_id: newId,
        category: item.category,
        price: item.price,
        currency: "BAM",
        user_id: u?.uid || item.sellerId || getMySellerId(),
        full_name: u?.displayName || username || "",
        email: u?.email || "",
      });
    // Trigger ShortPixel optimization. Give the request time to be sent before
    // redirecting so it is not aborted (large base64 payload can take a moment).
    fetch("/api/shortpixel-optimize-listing", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        listingId: newId,
        images: imageUrls,
      }),
      keepalive: true,
    }).catch((error) => {
      console.error(
        "[handlePublish] ShortPixel optimization request failed:",
        error
      );
    });

    // Delay redirect so the request body is actually sent; then redirect without waiting for optimization to finish.
    setTimeout(() => {
      window.location.href = "/";
    }, 2500);
    } catch (error) {
      console.error("[handlePublish] Error adding listing:", error);
      showToast.error("Greška pri dodavanju oglasa. Pokušaj ponovo.");
      publishLockRef.current = false;
      setIsPublishing(false);
    }
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setShowPreview(true);
  };

  if (showPreview) {
    return (
      <RequireAuth>
        <Header />
        <main className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10 space-y-4 sm:space-y-6 md:space-y-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[var(--text)] mb-1 sm:mb-2">Pregled oglasa</h1>
              <p className="text-[var(--muted)] text-xs sm:text-sm">
                Provjerite kako će vaš oglas izgledati prije objave
              </p>
            </div>
            <button
              onClick={() => setShowPreview(false)}
              className="px-3 sm:px-4 py-2 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--card)] text-[var(--text)] hover:bg-[var(--bg)] transition font-semibold text-xs sm:text-sm"
            >
              Nazad
            </button>
          </div>

          {/* Preview Card - Instagram style */}
          <div className="bg-[var(--card)] rounded-xl sm:rounded-2xl border border-[var(--border)] overflow-hidden shadow-lg">
            {/* Images or Video */}
            <div className="aspect-square bg-[var(--bg)]">
              {videoUrl ? (
                <video
                  src={videoUrl}
                  className="w-full h-full object-cover"
                  autoPlay
                  loop
                  muted
                  playsInline
                />
              ) : imageUrls.length > 0 ? (
                <img
                  src={imageUrls[0]}
                  alt={title}
                  className="w-full h-full object-cover"
                />
              ) : null}
            </div>

            {/* Content */}
            <div className="p-4 sm:p-5 md:p-6 space-y-3 sm:space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Avatar
                    userId={undefined}
                    displayName={username}
                    size="md"
                    className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10"
                  />
                  <div>
                    <div className="font-semibold text-sm sm:text-base text-[var(--text)]">{username}</div>
                    <div className="text-[10px] sm:text-xs text-[var(--muted)]">{city}</div>
                  </div>
                </div>
                <div className="text-xl sm:text-2xl font-extrabold text-[var(--accent)]">
                  {allowsTrade ? "Zamjena" : allowsOffers ? "Po dogovoru" : `${price} KM`}
                </div>
              </div>

              {/* Title and details */}
              <div>
                <h2 className="text-lg sm:text-xl font-extrabold text-[var(--text)] mb-1.5 sm:mb-2">{title}</h2>
                <div className="flex flex-wrap gap-1.5 sm:gap-2 text-xs sm:text-sm text-[var(--muted)]">
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-[var(--bg)]">{category}</span>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-[var(--bg)]">{productType}</span>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-[var(--bg)]">{brand}</span>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-[var(--bg)]">{size}</span>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-[var(--bg)]">{color}</span>
                  <span className="px-2 sm:px-3 py-0.5 sm:py-1 rounded-full bg-[var(--bg)]">{condition}</span>
                </div>
              </div>

              {/* Description */}
              {description.trim() && (
                <div className="pt-2 border-t border-[var(--border)]">
                  <p className="text-xs sm:text-sm text-[var(--text)] whitespace-pre-wrap leading-relaxed">
                    {description}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 md:gap-4">
            <button
              onClick={() => setShowPreview(false)}
              className="flex-1 px-4 sm:px-5 md:px-6 py-3 sm:py-3.5 md:py-4 rounded-lg sm:rounded-xl border-2 border-[var(--border)] bg-[var(--card)] text-[var(--text)] font-bold text-xs sm:text-sm md:text-base hover:bg-[var(--bg)] transition"
            >
              Nazad na uređivanje
            </button>
            <button
              onClick={handlePublish}
              disabled={Object.values(isUploading).some(v => v === true) || isPublishing}
              className="flex-1 px-4 sm:px-5 md:px-6 py-3 sm:py-3.5 md:py-4 rounded-lg sm:rounded-xl bg-[var(--accent)] text-white font-bold text-xs sm:text-sm md:text-base hover:opacity-90 transition shadow-sm hover:shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg width="18" height="18" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 5v14M5 12h14" />
              </svg>
              {isPublishing
                ? "Objavljujem..."
                : Object.values(isUploading).some(v => v === true) 
                ? "Učitavanje slika..." 
                : "Objavi oglas"}
            </button>
          </div>
        </main>
      </RequireAuth>
    );
  }

  return (
    <RequireAuth>
      <Header />
      <main className="max-w-4xl mx-auto px-3 sm:px-4 md:px-6 py-4 sm:py-6 md:py-10 space-y-4 sm:space-y-6 md:space-y-8">
        <div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-[var(--text)] mb-1 sm:mb-2">Dodaj novi oglas</h1>
          <p className="text-[var(--muted)] text-xs sm:text-sm">
            Popuni sve informacije o artiklu koji želiš prodati
          </p>
        </div>

        <form onSubmit={submit} className="bg-[var(--card)] rounded-xl sm:rounded-2xl border border-[var(--border)] p-4 sm:p-6 md:p-8 shadow-sm space-y-4 sm:space-y-5 md:space-y-6">
          <div className="grid md:grid-cols-2 gap-3 sm:gap-4 md:gap-5">
            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Naslov oglasa *</label>
              <input 
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border text-sm sm:text-base ${
                  errors.title ? "border-red-500" : "border-[var(--border)]"
                } bg-[var(--card)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] transition`}
                value={title} 
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) setErrors({ ...errors, title: undefined });
                }}
                placeholder="Npr. Crna jakna Zara" 
                required
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500">{errors.title}</p>
              )}
            </div>

            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Cijena, ponude ili zamjena</label>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setAllowsOffers(false);
                      setAllowsTrade(false);
                    }}
                    className={`flex-1 px-3 sm:px-4 py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition ${
                      !allowsOffers && !allowsTrade
                        ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                        : "border-[var(--border)] bg-[var(--card)] text-[var(--text)] hover:bg-[var(--bg)]"
                    }`}
                  >
                    Fiksna cijena
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAllowsOffers(true);
                      setAllowsTrade(false);
                    }}
                    className={`flex-1 px-3 sm:px-4 py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition ${
                      allowsOffers && !allowsTrade
                        ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                        : "border-[var(--border)] bg-[var(--card)] text-[var(--text)] hover:bg-[var(--bg)]"
                    }`}
                  >
                    Omogući ponude
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setAllowsOffers(false);
                      setAllowsTrade(true);
                    }}
                    className={`flex-1 px-3 sm:px-4 py-2.5 rounded-xl border text-xs sm:text-sm font-semibold transition ${
                      allowsTrade && !allowsOffers
                        ? "border-[var(--accent)] bg-[var(--accent)] text-white"
                        : "border-[var(--border)] bg-[var(--card)] text-[var(--text)] hover:bg-[var(--bg)]"
                    }`}
                  >
                    Zamjena
                  </button>
                </div>
                {!allowsOffers && !allowsTrade && (
                  <div>
                    <div className="relative">
                      <input 
                        type="text"
                        inputMode="decimal"
                        className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 pr-16 rounded-lg sm:rounded-xl border text-sm sm:text-base ${
                          errors.price ? "border-red-500" : "border-[var(--border)]"
                        } bg-[var(--card)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] transition`}
                        value={price} 
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9,.]/g, '');
                          setPrice(value);
                          if (errors.price) setErrors({ ...errors, price: undefined });
                        }}
                        required={!allowsOffers && !allowsTrade}
                        placeholder="0.00"
                      />
                      <span className="absolute right-3 sm:right-4 top-1/2 -translate-y-1/2 text-sm font-semibold text-[var(--muted)]">
                        KM
                      </span>
                    </div>
                    {errors.price && (
                      <p className="mt-1 text-sm text-red-500">{errors.price}</p>
                    )}
                  </div>
                )}
                {allowsOffers && (
                  <div className="p-3 rounded-lg bg-[var(--accent)]/10 border border-[var(--accent)]/30">
                    <p className="text-xs sm:text-sm text-[var(--text)]">
                      Korisnici će moći da pošalju ponude za ovaj oglas. Neće biti prikazana fiksna cijena.
                    </p>
                  </div>
                )}
                {allowsTrade && (
                  <div className="p-3 rounded-lg bg-[var(--accent)]/10 border border-[var(--accent)]/30">
                    <p className="text-xs sm:text-sm text-[var(--text)]">
                      Ovaj artikal je dostupan za zamjenu. Neće biti prikazana fiksna cijena.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Kategorija *</label>
              <select
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--card)] text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] transition ${category ? "text-[var(--text)]" : "text-[var(--muted)]"}`}
                value={category}
                onChange={(e)=>setCategory(e.target.value as any)}
                required
              >
                <option value="" disabled hidden>
                  Odaberi kategoriju
                </option>
                <option value="Žene">Žene</option>
                <option value="Muškarci">Muškarci</option>
                <option value="Djeca">Djeca</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Tip proizvoda *</label>
              <SearchableSelect
                value={productType}
                onChange={(value) => setProductType(value)}
                options={TYPES}
                placeholder="Odaberi tip proizvoda"
                allowCustom={false}
              />
            </div>

            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Marka *</label>
              <SearchableSelect
                value={brand}
              onChange={(value) => {
                setBrand(value);
              }}
                options={BRANDS}
              placeholder="Upiši ili odaberi marku"
              allowCustom={true}
              />
            </div>

            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Veličina *</label>
              <SearchableSelect
                value={size}
                onChange={(value) => setSize(value)}
                options={SIZES}
                placeholder="Odaberi veličinu"
                allowCustom={false}
              />
            </div>

            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Boja *</label>
              <SearchableSelect
                value={color}
                onChange={(value) => setColor(value)}
                options={COLORS}
                placeholder="Odaberi boju"
                allowCustom={false}
              />
            </div>

            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Stanje *</label>
              <select
                className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border border-[var(--border)] bg-[var(--card)] text-sm sm:text-base focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] transition ${condition ? "text-[var(--text)]" : "text-[var(--muted)]"}`}
                value={condition}
                onChange={(e)=>setCondition(e.target.value as any)}
                required
              >
                <option value="" disabled hidden>
                  Odaberi stanje
                </option>
                <option value="Novo">Novo</option>
                <option value="Kao novo">Kao novo</option>
                <option value="Dobro">Dobro</option>
                <option value="Korektno">Korektno</option>
              </select>
            </div>

            <div>
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Grad *</label>
              <SearchableSelect
                value={city}
                onChange={(value) => setCity(value)}
                options={CITIES_BIH}
                placeholder="Odaberi grad"
                allowCustom={false}
              />
            </div>

            <div className="md:col-span-2">
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Slike proizvoda *</label>
              <p className="text-[10px] sm:text-xs text-[var(--muted)] mb-1.5 sm:mb-2">Najmanje jedna slika je obavezna. Možete dodati do 10 slika. Video je opcionalan.</p>
              <div 
                className={`border-2 border-dashed ${
                  errors.imageUrl ? "border-red-500" : isDragOver ? "border-[var(--accent)] bg-[var(--accent)]/10" : "border-[var(--border)]"
                } rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 text-center hover:border-[var(--accent)]/40 transition relative`}
                onDrop={handleDropFiles}
                onDragOver={handleDragOverFiles}
                onDragLeave={handleDragLeaveFiles}
              >
                <input
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  className="hidden"
                  id="image-upload"
                  multiple
                  onChange={(e) => onPickImages(e.target.files)}
                  disabled={Object.values(isUploading).some(v => v)}
                />
                <label htmlFor="image-upload" className={`cursor-pointer ${Object.values(isUploading).some(v => v) ? "pointer-events-none opacity-50" : ""}`}>
                  {imageUrls.length > 0 ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                        {imageUrls.map((url, index) => (
                          <div 
                            key={index} 
                            className={`relative cursor-move transition-all ${
                              draggedIndex === index ? "opacity-50 scale-95" : ""
                            } ${
                              dragOverIndex === index ? "ring-2 ring-[var(--accent)] scale-105" : ""
                            }`}
                            draggable
                            onDragStart={() => handleDragStart(index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDragLeave={handleDragLeave}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            <div className="relative">
                              <img 
                                src={url} 
                                alt={`preview ${index + 1}`} 
                                className="w-full h-32 sm:h-40 md:h-48 object-cover rounded-lg sm:rounded-xl border border-[var(--border)] shadow-sm pointer-events-none" 
                              />
                              {isUploading[index] && (
                                <div className="absolute inset-0 bg-black/50 rounded-lg sm:rounded-xl flex items-center justify-center">
                                  <div className="text-white font-semibold text-xs">{uploadProgress[index] || 0}%</div>
                                </div>
                              )}
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removeImage(index);
                                }}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 transition shadow-lg z-10"
                                title="Obriši sliku"
                              >
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                  <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                              </button>
                              {index === 0 && (
                                <div className="absolute bottom-1 left-1 bg-[var(--accent)] text-white text-[10px] px-1.5 py-0.5 rounded font-semibold">
                                  Glavna
                                </div>
                              )}
                              <div className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded font-semibold flex items-center gap-1">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <path d="M3 12h18M12 3v18" />
                                </svg>
                                {index + 1}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      {imageUrls.length < 10 && (
                        <div>
                          <p className="text-xs sm:text-sm text-[var(--accent)] font-semibold mb-1">Klikni za dodavanje više slika ({imageUrls.length}/10)</p>
                          <p className="text-[10px] sm:text-xs text-[var(--muted)]/70">Ili prevuci slike ovdje • Možeš promijeniti redoslijed prevlačenjem</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3">
                      <svg className="mx-auto h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-[var(--muted)]/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <p className="text-xs sm:text-sm text-[var(--muted)] font-semibold">Klikni ili prevuci slike ovdje</p>
                      <p className="text-[10px] sm:text-xs text-[var(--muted)]/70">Preporučeno: JPG ili PNG, max 5MB po slici, do 10 slika</p>
                    </div>
                  )}
                </label>
              </div>
              {errors.imageUrl && (
                <p className="mt-1 text-xs sm:text-sm text-red-500">{errors.imageUrl}</p>
              )}
            </div>

            <div className="md:col-span-2">
              <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">Video proizvoda (opcionalno)</label>
              <div className={`border-2 border-dashed border-[var(--border)] rounded-lg sm:rounded-xl p-3 sm:p-4 md:p-6 text-center hover:border-[var(--accent)]/40 transition relative`}>
                <input
                  type="file"
                  accept="video/mp4,video/webm,video/quicktime"
                  className="hidden"
                  id="video-upload"
                  onChange={(e) => onPickVideo(e.target.files?.[0] ?? null)}
                  disabled={isUploadingVideo}
                />
                <label htmlFor="video-upload" className={`cursor-pointer ${isUploadingVideo ? "pointer-events-none opacity-50" : ""}`}>
                  {videoUrl || videoPreview ? (
                    <div className="space-y-3 relative">
                      <div className="relative">
                        {videoPreview ? (
                          <video
                            src={videoPreview}
                            className="mx-auto max-h-48 sm:max-h-56 md:max-h-64 object-cover rounded-lg sm:rounded-xl border border-[var(--border)] shadow-sm"
                            controls
                            muted
                          />
                        ) : videoUrl ? (
                          <video
                            src={videoUrl}
                            className="mx-auto max-h-48 sm:max-h-56 md:max-h-64 object-cover rounded-lg sm:rounded-xl border border-[var(--border)] shadow-sm"
                            controls
                            muted
                          />
                        ) : null}
                        {isUploadingVideo && (
                          <div className="absolute inset-0 bg-black/50 rounded-lg sm:rounded-xl flex items-center justify-center">
                            <div className="text-white font-semibold text-xs sm:text-sm">Učitavanje videa...</div>
                          </div>
                        )}
                        {(videoUrl || videoPreview) && !isUploadingVideo && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeVideo();
                            }}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition shadow-lg"
                            title="Obriši video"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M18 6L6 18M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                      {!isUploadingVideo && (
                        <p className="text-xs sm:text-sm text-[var(--accent)] font-semibold">Klikni za promjenu videa</p>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <svg className="mx-auto h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 text-[var(--muted)]/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      <p className="text-xs sm:text-sm text-[var(--muted)] font-semibold">Klikni za dodavanje videa</p>
                      <p className="text-[10px] sm:text-xs text-[var(--muted)]/70">Preporučeno: MP4, max 50MB</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>

          <div>
            <label className="block font-semibold text-xs sm:text-sm mb-1.5 sm:mb-2 text-[var(--text)]">
              Opis proizvoda
              {description.trim().length > 0 && (
                <span className="ml-2 text-[var(--muted)] font-normal text-[10px] sm:text-xs">
                  ({description.trim().length}/{MAX_DESCRIPTION_LENGTH})
                </span>
              )}
            </label>
            <textarea 
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 md:py-3 rounded-lg sm:rounded-xl border text-sm sm:text-base ${
                errors.description ? "border-red-500" : "border-[var(--border)]"
              } bg-[var(--card)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/30 focus:border-[var(--accent)] transition min-h-[100px] sm:min-h-[120px] resize-none`}
              value={description} 
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) setErrors({ ...errors, description: undefined });
              }}
              placeholder="Npr. nošena par puta, bez oštećenja, odlično očuvana..." 
              maxLength={MAX_DESCRIPTION_LENGTH}
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-500">{errors.description}</p>
            )}
            <p className="text-[10px] sm:text-xs text-[var(--muted)] mt-1.5 sm:mt-2">Dodaj detalje o stanju i karakteristikama proizvoda</p>
          </div>

          <div className={`bg-[var(--accent)]/10 rounded-lg sm:rounded-xl p-3 sm:p-4 border ${
            errors.agree ? "border-red-500" : "border-[var(--accent)]/30"
          }`}>
            <label className="flex items-start gap-2 sm:gap-3 text-xs sm:text-sm cursor-pointer">
              <input 
                type="checkbox" 
                checked={agree} 
                onChange={(e) => {
                  setAgree(e.target.checked);
                  if (errors.agree) setErrors({ ...errors, agree: undefined });
                }}
                className="mt-1 w-4 h-4 text-[var(--accent)] border-[var(--border)] rounded focus:ring-[var(--accent)]"
                required
              />
              <span className="text-[var(--text)]">
                Slažem se sa <a className="underline font-semibold text-[var(--accent)] hover:opacity-80" href="/terms">Uslovima korištenja</a> i <a className="underline font-semibold text-[var(--accent)] hover:opacity-80" href="/privacy">Politikom privatnosti</a>. *
              </span>
            </label>
            {errors.agree && (
              <p className="mt-2 text-sm text-red-500">{errors.agree}</p>
            )}
          </div>

          <button 
            type="submit"
            className="w-full py-3 sm:py-3.5 md:py-4 rounded-lg sm:rounded-xl font-bold text-sm sm:text-base text-white bg-[var(--accent)] hover:opacity-90 transition shadow-sm hover:shadow-md flex items-center justify-center gap-2"
          >
            <svg width="18" height="18" className="sm:w-5 sm:h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            Pregledaj oglas
          </button>
        </form>
      </main>
    </RequireAuth>
  );
}
